export interface ContextMenuItem {
  label: string;
  icon?: string;
  danger?: boolean;
  divider?: boolean;
  action?: () => void;
}

let activeMenu: HTMLElement | null = null;
let closeHandler: ((e: MouseEvent) => void) | null = null;
let keyHandler: ((e: KeyboardEvent) => void) | null = null;
let scrollHandler: (() => void) | null = null;

export function closeContextMenu(immediate = false): void {
  const menu = activeMenu;
  activeMenu = null;
  if (closeHandler) {
    document.removeEventListener('click', closeHandler);
    closeHandler = null;
  }
  if (keyHandler) {
    document.removeEventListener('keydown', keyHandler);
    keyHandler = null;
  }
  if (scrollHandler) {
    document.removeEventListener('scroll', scrollHandler, true);
    scrollHandler = null;
  }
  if (!menu) return;
  // Mobile bottom-sheet: slide the menu back down (200ms) before removing,
  // mirroring @keyframes mob-sheet-in. Desktop closes instantly.
  const isMobileSheet = window.matchMedia('(max-width: 540px)').matches;
  if (immediate || !isMobileSheet) {
    menu.remove();
    return;
  }
  menu.classList.add('mob-sheet-closing');
  let removed = false;
  const cleanup = (): void => {
    if (removed) return;
    removed = true;
    menu.remove();
  };
  menu.addEventListener('animationend', cleanup, { once: true });
  window.setTimeout(cleanup, 240);
}

export function showContextMenu(x: number, y: number, items: ContextMenuItem[]): void {
  // Immediate close of any prior menu — no overlap while opening the next.
  closeContextMenu(true);

  const menu = document.createElement('div');
  menu.className = 'context-menu';

  // Discord-mobile grabber pill — CSS hides this on ≥768px, shows it when
  // the menu reflows into a bottom sheet on phones.
  const handle = document.createElement('div');
  handle.className = 'context-menu-handle';
  handle.setAttribute('aria-hidden', 'true');
  menu.appendChild(handle);

  for (const item of items) {
    if (item.divider) {
      const div = document.createElement('div');
      div.className = 'context-menu-divider';
      menu.appendChild(div);
      continue;
    }
    const el = document.createElement('div');
    el.className = `context-menu-item${item.danger ? ' danger' : ''}`;
    el.innerHTML = `${item.icon ?? ''}${escHtml(item.label)}`;
    if (item.action) {
      const action = item.action;
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        closeContextMenu();
        action();
      });
    }
    menu.appendChild(el);
  }

  // Position: ensure menu stays in viewport
  menu.style.left = `${x}px`;
  menu.style.top = `${y}px`;
  document.body.appendChild(menu);
  activeMenu = menu;

  // Adjust if off screen
  requestAnimationFrame(() => {
    const rect = menu.getBoundingClientRect();
    if (rect.right > window.innerWidth) {
      menu.style.left = `${window.innerWidth - rect.width - 8}px`;
    }
    if (rect.bottom > window.innerHeight) {
      menu.style.top = `${window.innerHeight - rect.height - 8}px`;
    }
  });

  // Close on click outside
  closeHandler = () => closeContextMenu();
  setTimeout(() => {
    if (closeHandler) document.addEventListener('click', closeHandler);
  }, 0);

  // Close on Escape
  keyHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') closeContextMenu();
  };
  document.addEventListener('keydown', keyHandler);

  // Close on scroll (anchor becomes stale)
  scrollHandler = () => closeContextMenu();
  document.addEventListener('scroll', scrollHandler, { capture: true, passive: true });
}

function escHtml(str: string): string {
  const el = document.createElement('div');
  el.textContent = str;
  return el.innerHTML;
}
